import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { BookWorkshopDeliveryOptionInfoText, IBooking } from '../models/book-workshop.models';
import { STORAGE_SERVICE_KEYS, StorageService } from './storage.service';

@Injectable({
    providedIn: 'root',
})
export class BookWorkshopFlowService {
    private readonly _emptyBooking: IBooking = {
        address: '',
        city: '',
        comment: '',
        delivery: '',
        email: '',
        firstName: '',
        lastName: '',
        modelName: '',
        optionId: '',
        phone: '',
        regNumber: '',
        services: [],
        zipCode: '',
        pickupData: {
            address: '',
            time: '',
            zipCode: '',
        },
        rentalCar: false,
        rentalCarClassName: ''
    };

    private readonly _booking$ = new BehaviorSubject<IBooking>({ ...this._emptyBooking });
    booking$ = this._booking$.asObservable();

    private readonly _step$ = new BehaviorSubject(0);
    step$ = this._step$.asObservable();

    deliveryOptionInfoTexts: BookWorkshopDeliveryOptionInfoText[];
    selectedDeliveryOptionInfoText$ = new BehaviorSubject<BookWorkshopDeliveryOptionInfoText | undefined>(undefined);

    constructor(private storageService: StorageService) {}

    nextStep(stepData: Partial<IBooking>) {
        // if shop changes reset services
        if (stepData.shop && stepData.shop !== this._booking$.value.shop) {
            stepData = {
                ...stepData,
                services: [],
            };
        }

        // If delivery option, find the corresponding delivery option info text
        if (stepData.optionId) {
            const selectedOptionInfoText = this.deliveryOptionInfoTexts.filter((textItem: BookWorkshopDeliveryOptionInfoText) => stepData.optionId === textItem.optionID)[0] ?? '';
            this.selectedDeliveryOptionInfoText$.next(selectedOptionInfoText);
        }

        this.saveBooking(stepData);
        this._step$.next(this._step$.value + 1);
    }

    prevStep(stepData?: Partial<IBooking>) {
        if (stepData) {
            this.saveBooking(stepData);
        }

        this._step$.next(this._step$.value - 1);
    }

    resetStep() {
        this._step$.next(0);
    }

    resetBooking() {
        this._booking$.next({ ...this._emptyBooking });
        this.storageService.removeItem(STORAGE_SERVICE_KEYS.BOOK_WORKSHOP, true);
    }

    initBooking() {
        const storedBooking = this.storageService.getItem(STORAGE_SERVICE_KEYS.BOOK_WORKSHOP, true);
        if (storedBooking) {
            const parsedBooking: IBooking = JSON.parse(storedBooking);
            this._booking$.next(parsedBooking);
        }
    }

    saveBooking(bookingData: Partial<IBooking>) {
        const updatedBooking = {
            ...this._booking$.getValue(),
            ...bookingData,
        };

        this._booking$.next(updatedBooking);
        this.storageService.setItem(STORAGE_SERVICE_KEYS.BOOK_WORKSHOP, JSON.stringify(updatedBooking), true);
    }
}
